/**
 * Busca dos focos no INPE para a janela do loop.
 *
 * - 10 min (GOES-19): lê a listagem Apache, escolhe os arquivos com horário no
 *   nome em [início, fim + 90 min], baixa com pool de 6 conexões (cache em
 *   memória por arquivo: na atualização só baixa os novos), usa SEMPRE a
 *   coluna `data` como instante e deduplica por lat|lon|data.
 * - Diário (todos os satélites): um arquivo por dia UTC da janela, lido num
 *   Web Worker; o arquivo do dia corrente é revalidado (`cache: 'no-cache'`).
 */
import { emptyTable, mergeDedup10min, parse10minCsv } from './csv';
import { fetchDailyTable, type DailyFileRequest, type DailyFileResult } from './dailyLoader';
import type { WorkerRequest, WorkerResponse } from './dailyWorker';
import { formatBytes } from '../lib/format';
import { formatShortDateBrt, formatTimeBrt } from '../lib/time';
import {
  DAY,
  MINUTE,
  TENMIN_DIR_URL,
  dailyUrl,
  isDailyFileMutable,
  parseApacheListing,
  selectTenMinFiles,
  TENMIN_ATRASO_ARQUIVO,
  tenMinUrl,
  utcDaysInRange,
  type TenMinFile,
} from './inpe';
import { HttpError, abortError, abortable, fetchWithRetry, isAbortError, runPool } from './net';
import { projectTable } from './project';
import type { TenMinAmazoniaTest } from './amazonia';
import type { FireTable, RawFire10, TimeWindow } from './types';

/** Satélite dos arquivos de 10 min que exibimos (os demais vêm no diário). */
export const TENMIN_SATELLITE = 'GOES-19';
export const TENMIN_CONCURRENCY = 6;
/** Janela máxima coberta pelo diário (7 dias = até 8 arquivos UTC de ~3,6 MB). */
export const MAX_DAILY_WINDOW_DAYS = 7;
export const DAILY_FILE_ESTIMATE = 3.6 * 1024 * 1024;

export interface Progress {
  done: number;
  total: number;
  /** Bytes baixados até agora. */
  bytes: number;
}

// ---------------------------------------------------------------------------
// 10 min
// ---------------------------------------------------------------------------

export interface TenMinResult {
  /** Focos GOES-19 deduplicados, ordenados por data. */
  fires: RawFire10[];
  /** Arquivos usados / baixados agora (os demais vieram do cache). */
  files: number;
  downloaded: number;
  bytes: number;
  /** Primeiro e último arquivo existentes na listagem. */
  oldestAvailable: number | null;
  newestAvailable: number | null;
  /**
   * Até quando o INPE já publicou os focos: o horário das detecções do
   * arquivo mais novo LIDO (vazio ou não), ou `null` sem nenhum.
   */
  publicadoAte: number | null;
  warning: string | null;
  /** Arquivos que vieram com problema e foram ignorados (tentados de novo na próxima carga). */
  skipped: number;
  /** Aviso discreto sobre os arquivos ignorados. */
  note: string | null;
}

/** Aviso discreto para arquivos do INPE ignorados. */
export function skippedFilesNote(n: number): string | null {
  if (n <= 0) return null;
  return n === 1
    ? '1 arquivo do INPE veio com problema e foi ignorado (nova tentativa na próxima atualização).'
    : `${n} arquivos do INPE vieram com problema e foram ignorados (nova tentativa na próxima atualização).`;
}

/** Cache em memória: nome do arquivo → focos lidos (arquivos publicados não mudam). */
const tenMinCache = new Map<string, RawFire10[]>();

export function clearTenMinCache(): void {
  tenMinCache.clear();
}

export async function load10min(
  range: TimeWindow,
  opts: { signal?: AbortSignal; onProgress?: (p: Progress) => void; fetchImpl?: typeof fetch } = {},
): Promise<TenMinResult> {
  const { signal, onProgress, fetchImpl } = opts;
  const listingRes = await fetchWithRetry(TENMIN_DIR_URL, { signal, cache: 'no-cache', fetchImpl });
  if (!listingRes) throw new HttpError(404, TENMIN_DIR_URL);
  const listing = parseApacheListing(await listingRes.text());
  const oldest = listing[0]?.t ?? null;
  const newest = listing.at(-1)?.t ?? null;

  // Descarta do cache os arquivos que já saíram da listagem.
  const names = new Set(listing.map((f) => f.name));
  for (const k of tenMinCache.keys()) if (!names.has(k)) tenMinCache.delete(k);

  const selected = selectTenMinFiles(listing, range);
  const missing = selected.filter((f) => !tenMinCache.has(f.name));
  let done = selected.length - missing.length;
  let bytes = 0;
  const failures: unknown[] = [];
  const results404 = new Map<string, RawFire10[]>();
  onProgress?.({ done, total: selected.length, bytes });

  await runPool(
    missing,
    TENMIN_CONCURRENCY,
    async (file: TenMinFile) => {
      // Cada arquivo é independente: um arquivo ruim (HTML no lugar do CSV,
      // falha de rede após as novas tentativas) é ignorado, avisado e NÃO vai
      // para o cache, para ser tentado de novo na próxima carga. Um 404 de
      // arquivo listado (removido entre a listagem e o download) conta como
      // "sem focos", sem aviso, e também não vai para o cache.
      let fires: RawFire10[] | null = null;
      let cacheable = true;
      try {
        const res = await fetchWithRetry(tenMinUrl(file.name), { signal, fetchImpl });
        if (res) {
          const text = await res.text();
          bytes += text.length;
          fires = parse10minCsv(text, { satellite: TENMIN_SATELLITE }).fires;
        } else {
          fires = [];
          cacheable = false;
        }
      } catch (e) {
        if (isAbortError(e) || signal?.aborted) throw abortError();
        failures.push(e);
      }
      if (signal?.aborted) throw abortError();
      if (fires) {
        if (cacheable) tenMinCache.set(file.name, fires);
        else results404.set(file.name, fires);
      }
      done++;
      onProgress?.({ done, total: selected.length, bytes });
    },
    signal,
  );

  // Nada pôde ser lido (ex.: sem conexão): aí sim é erro.
  if (failures.length && failures.length === selected.length) {
    throw failures[0] instanceof Error ? failures[0] : new Error(String(failures[0]));
  }
  if (failures.length) console.warn(`[focos] ${failures.length} arquivo(s) de 10 min ignorado(s)`, failures);

  const fires = mergeDedup10min(selected.map((f) => tenMinCache.get(f.name) ?? results404.get(f.name) ?? []));

  let warning: string | null = null;
  if (oldest !== null && range.start < oldest - 10 * MINUTE) {
    warning =
      `Os arquivos a cada 10 min só cobrem cerca dos últimos 3 dias (desde ${formatShortDateBrt(oldest)} ${formatTimeBrt(oldest)}, Brasília). ` +
      'Para datas anteriores, use a fonte diária.';
  } else if (newest !== null && range.start > newest) {
    warning = 'Ainda não há arquivos de 10 min para este período.';
  }
  return {
    fires,
    files: selected.length,
    downloaded: missing.length - failures.length - results404.size,
    bytes,
    oldestAvailable: oldest,
    newestAvailable: newest,
    publicadoAte: publicadoAte(selected, (f) => tenMinCache.has(f.name) || results404.has(f.name)),
    warning,
    skipped: failures.length,
    note: skippedFilesNote(failures.length),
  };
}

/**
 * Instante das detecções do arquivo mais novo que foi lido — um arquivo
 * vazio (só o cabeçalho) conta: é uma hora sem focos, já publicada. Os que
 * falharam não contam.
 */
export function publicadoAte(arquivos: readonly TenMinFile[], lido: (f: TenMinFile) => boolean): number | null {
  let t: number | null = null;
  for (const f of arquivos) if (lido(f) && (t === null || f.t > t)) t = f.t;
  return t === null ? null : t - TENMIN_ATRASO_ARQUIVO;
}

/** Monta a tabela (projetada) dos focos de 10 min, marcando os da Amazônia Legal. */
export function buildTenMinTable(fires: RawFire10[], inAmazonia: TenMinAmazoniaTest): FireTable {
  const table = emptyTable('goes10min', false, fires.length);
  const satIndex = new Map<string, number>();
  fires.forEach((f, i) => {
    table.lat[i] = f.lat;
    table.lon[i] = f.lon;
    table.t[i] = f.t;
    let s = satIndex.get(f.satelite);
    if (s === undefined) {
      s = satIndex.size;
      satIndex.set(f.satelite, s);
    }
    table.sat[i] = s;
  });
  table.dict.sat = [...satIndex.keys()];
  projectTable(table);
  for (let i = 0; i < table.length; i++) {
    table.amz[i] = inAmazonia(table.lon[i]!, table.lat[i]!, table.x[i]!, table.y[i]!) ? 1 : 0;
  }
  return table;
}

/** Recalcula as marcas de Amazônia Legal (quando o polígono chega depois). */
export function remarkAmazonia(table: FireTable, inAmazonia: TenMinAmazoniaTest): FireTable {
  const amz = new Uint8Array(table.length);
  for (let i = 0; i < table.length; i++) {
    amz[i] = inAmazonia(table.lon[i]!, table.lat[i]!, table.x[i]!, table.y[i]!) ? 1 : 0;
  }
  return { ...table, amz };
}

// ---------------------------------------------------------------------------
// Diário
// ---------------------------------------------------------------------------

interface DailyEntry {
  day: number;
  table: FireTable;
  lastModified: string | null;
  length: string | null;
  bytes: number;
  /** Códigos usados no filtro quando o arquivo foi lido (para invalidar). */
  codesKey: string;
}

const dailyCache = new Map<number, DailyEntry>();

/**
 * Downloads de dias em andamento, compartilhados entre cargas: se a janela
 * muda (ou chega um `refreshFires`) no meio do download de um dia que continua
 * necessário, a nova carga espera o MESMO download em vez de baixar de novo.
 * Só são cancelados quando o dia sai da janela (ou na parada do módulo).
 */
interface DayJob {
  key: string;
  ac: AbortController;
  promise: Promise<DailyFileResult>;
  listeners: Set<(loaded: number, total: number) => void>;
}
const dayJobs = new Map<number, DayJob>();

export function clearDailyCache(): void {
  abortDailyJobs();
  dailyCache.clear();
}

/** Cancela todos os downloads do diário em andamento. */
export function abortDailyJobs(): void {
  for (const job of dayJobs.values()) job.ac.abort();
  dayJobs.clear();
}

function startDayJob(day: number, req: DailyFileRequest, codesKey: string): DayJob {
  const key = `${req.url}|${req.cache}|${codesKey}`;
  const existing = dayJobs.get(day);
  if (existing && existing.key === key) return existing;
  existing?.ac.abort();
  const ac = new AbortController();
  const listeners = new Set<(loaded: number, total: number) => void>();
  const promise = dailyClient
    .load(req, ac.signal, (l, t) => listeners.forEach((fn) => fn(l, t)))
    .then((res) => {
      // O cache é atualizado mesmo que nenhuma carga esteja esperando mais.
      if (res.status === 'ok') {
        dailyCache.set(day, { day, table: res.table, lastModified: res.lastModified, length: res.length, bytes: res.bytes, codesKey });
      } else if (res.status === 'missing') {
        dailyCache.delete(day);
      }
      return res;
    })
    .finally(() => {
      if (dayJobs.get(day) === job) dayJobs.delete(day);
    });
  promise.catch(() => {}); // sem "unhandled rejection" se ninguém espera
  const job: DayJob = { key, ac, promise, listeners };
  dayJobs.set(day, job);
  return job;
}

export interface DailyResult {
  /** Uma tabela por dia, em ordem cronológica. */
  tables: FireTable[];
  days: number[];
  /** Bytes baixados agora. */
  bytes: number;
  downloaded: number;
  warning: string | null;
  /** Dias que vieram com problema e foram ignorados (ou mantidos na versão anterior). */
  skipped: number;
  note: string | null;
}

/** Dias UTC do diário para a janela (limitada aos últimos MAX_DAILY_WINDOW_DAYS dias). */
export function dailyDaysForRange(range: TimeWindow, now = Date.now()): { days: number[]; truncated: boolean } {
  const start = Math.max(range.start, range.end - MAX_DAILY_WINDOW_DAYS * DAY);
  const days = utcDaysInRange({ start, end: range.end }).filter((d) => d <= now);
  return { days, truncated: start > range.start };
}

export async function loadDaily(
  range: TimeWindow,
  opts: {
    signal?: AbortSignal;
    onProgress?: (p: Progress) => void;
    codes: number[] | null;
    now?: number;
  },
): Promise<DailyResult> {
  const { signal, onProgress, codes } = opts;
  const now = opts.now ?? Date.now();
  const { days, truncated } = dailyDaysForRange(range, now);
  const codesKey = codes ? `n${codes.length}` : 'aprox';
  if (signal?.aborted) throw abortError();

  // Cancelamento limpo: dias que saíram da janela não precisam mais ser baixados.
  for (const [d, job] of dayJobs) {
    if (!days.includes(d)) {
      job.ac.abort();
      dayJobs.delete(d);
    }
  }

  const loaded = new Map<number, number>();
  const totals = new Map<number, number>();
  let bytes = 0;
  let downloaded = 0;
  const failures: unknown[] = [];
  const report = () => {
    let l = 0;
    let t = 0;
    for (const d of days) {
      const tot = totals.get(d) ?? DAILY_FILE_ESTIMATE;
      t += tot;
      l += Math.min(loaded.get(d) ?? 0, tot);
    }
    onProgress?.({ done: l, total: t, bytes: l });
  };
  report();

  const results = new Map<number, FireTable>();
  await runPool(
    days,
    2,
    async (day) => {
      const cachedEntry = dailyCache.get(day);
      const cached = cachedEntry && cachedEntry.codesKey === codesKey ? cachedEntry : null;
      const mutable = isDailyFileMutable(day, now);
      const finish = () => {
        totals.set(day, totals.get(day) ?? 1);
        loaded.set(day, totals.get(day)!);
        report();
      };
      if (cached && !mutable) {
        results.set(day, cached.table);
        finish();
        return;
      }
      const job = startDayJob(
        day,
        {
          url: dailyUrl(day),
          cache: mutable ? 'no-cache' : 'default',
          known: cached ? { lastModified: cached.lastModified, length: cached.length } : null,
          codes,
        },
        codesKey,
      );
      const onProgressDay = (l: number, t: number) => {
        loaded.set(day, l);
        if (t > 0) totals.set(day, t);
        report();
      };
      job.listeners.add(onProgressDay);
      try {
        const res = await abortable(job.promise, signal);
        if (res.status === 'ok') {
          bytes += res.bytes;
          downloaded++;
          results.set(day, res.table);
        } else if (res.status === 'unchanged') {
          const table = cached?.table ?? dailyCache.get(day)?.table;
          if (table) results.set(day, table);
        }
        // 'missing': ainda não publicado (ou removido) — dia sem focos.
      } catch (e) {
        if (signal?.aborted) throw abortError();
        // Um dia ruim (HTML no lugar do CSV, rede, worker) não derruba os outros;
        // se havia versão em memória, ela continua valendo.
        failures.push(e);
        if (cached) results.set(day, cached.table);
      } finally {
        job.listeners.delete(onProgressDay);
      }
      finish();
    },
    signal,
  );

  if (failures.length && results.size === 0) {
    throw failures[0] instanceof Error ? failures[0] : new Error(String(failures[0]));
  }
  if (failures.length) console.warn(`[focos] ${failures.length} arquivo(s) diário(s) ignorado(s)`, failures);

  // Descarta dias fora da janela para não acumular memória.
  for (const d of dailyCache.keys()) if (d < days[0]! - DAY || d > days.at(-1)! + DAY) dailyCache.delete(d);

  let warning: string | null = null;
  if (truncated) {
    warning =
      `Período longo: para não baixar demais, o diário mostra só os últimos ${MAX_DAILY_WINDOW_DAYS} dias da janela ` +
      `(~${formatBytes(days.length * DAILY_FILE_ESTIMATE)}).`;
  }
  return {
    tables: days.map((d) => results.get(d)).filter((t): t is FireTable => !!t),
    days,
    bytes,
    downloaded,
    warning,
    skipped: failures.length,
    note: skippedFilesNote(failures.length),
  };
}

/** Estimativa do download do diário para a janela (para avisar o cidadão). */
export function estimateDailyDownload(range: TimeWindow, now = Date.now()): { files: number; bytes: number } {
  const { days } = dailyDaysForRange(range, now);
  const files = days.filter((d) => !dailyCache.has(d) || isDailyFileMutable(d, now)).length;
  return { files, bytes: files * DAILY_FILE_ESTIMATE };
}

// ---------------------------------------------------------------------------
// Cliente do Web Worker (com alternativa na thread principal)
// ---------------------------------------------------------------------------

type Pending = {
  resolve: (r: DailyFileResult) => void;
  reject: (e: Error) => void;
  onProgress?: (loaded: number, total: number) => void;
};

class DailyWorkerClient {
  private worker: Worker | null = null;
  private broken = false;
  private seq = 0;
  private readonly pending = new Map<number, Pending>();

  private getWorker(): Worker | null {
    if (this.broken || typeof Worker === 'undefined') return null;
    if (!this.worker) {
      try {
        this.worker = new Worker(new URL('./dailyWorker.ts', import.meta.url), { type: 'module' });
        this.worker.onmessage = (e: MessageEvent<WorkerResponse>) => this.onMessage(e.data);
        this.worker.onerror = (e) => {
          console.warn('[focos] worker do diário falhou; lendo na thread principal', e.message);
          this.broken = true;
          this.worker?.terminate();
          this.worker = null;
          for (const p of this.pending.values()) p.reject(new Error('worker indisponível'));
          this.pending.clear();
        };
      } catch {
        this.broken = true;
        return null;
      }
    }
    return this.worker;
  }

  private onMessage(msg: WorkerResponse) {
    const p = this.pending.get(msg.id);
    if (!p) return;
    if (msg.type === 'progress') {
      p.onProgress?.(msg.loaded, msg.total);
      return;
    }
    this.pending.delete(msg.id);
    if (msg.type === 'result') p.resolve(msg.result);
    else p.reject(msg.aborted ? abortError() : new Error(msg.message));
  }

  async load(
    req: DailyFileRequest,
    signal: AbortSignal | undefined,
    onProgress?: (loaded: number, total: number) => void,
  ): Promise<DailyFileResult> {
    if (signal?.aborted) throw abortError();
    const worker = this.getWorker();
    if (!worker) return fetchDailyTable(req, { signal, onProgress });
    const id = ++this.seq;
    try {
      return await new Promise<DailyFileResult>((resolve, reject) => {
        this.pending.set(id, { resolve, reject, onProgress });
        signal?.addEventListener(
          'abort',
          () => {
            worker.postMessage({ type: 'abort', id } satisfies WorkerRequest);
            this.pending.delete(id);
            reject(abortError());
          },
          { once: true },
        );
        worker.postMessage({ type: 'load', id, req } satisfies WorkerRequest);
      });
    } catch (e) {
      // Falha do próprio worker (não do download): tenta na thread principal.
      if (!isAbortError(e) && this.broken) return fetchDailyTable(req, { signal, onProgress });
      throw e;
    }
  }
}

const dailyClient = new DailyWorkerClient();
